import { type ReactNode } from "react";

type CompanyPanelHeaderProps = {
  avatar: ReactNode;
  name: string;
  /** Line under the name (the kind of business, the city…). */
  subtitle?: ReactNode;
  /** Badges on the line under the name. */
  badges?: ReactNode;
  actions?: ReactNode;
  className?: string;
  /** Everything below the identity row: CTAs, activity box, links. */
  children?: ReactNode;
};

/**
 * Header of the company side panel: round avatar, name, badge line and
 * optional actions, then whatever the caller stacks below. Shared by the
 * company panel and the web preview so both read as the same fiche.
 */
export default function CompanyPanelHeader({
  avatar,
  name,
  subtitle,
  badges,
  actions,
  className = "bg-gradient-to-br from-primary/5 to-transparent px-6 pt-8 pb-6 border-b border-border",
  children,
}: CompanyPanelHeaderProps) {
  return (
    <div className={className}>
      <div className="flex items-start gap-4 pr-6">
        {avatar}
        <div className="flex-1 min-w-0 space-y-1.5">
          <h2 className="text-2xl font-bold tracking-tight truncate">{name}</h2>
          {subtitle && (
            <p className="text-sm text-muted-foreground truncate">{subtitle}</p>
          )}
          <div className="flex flex-wrap items-center gap-2">{badges}</div>
        </div>
        {actions && <div className="shrink-0 flex items-center">{actions}</div>}
      </div>
      {children}
    </div>
  );
}
